import { Range } from 'slate';
import { cloneDeep, debounce } from 'lodash';
import Annotations from './Annotations';
import { LOCAL_ONLY_ANNOTATION_KEY } from './utils';
import type {
  Annotation,
  AnnotationKey,
  EditorMetadata,
  EditorPlugin,
} from '../../types/aha-editor';

import type AhaEditor from '../../AhaEditor';
import { isAnnotationKey } from '../../types/guards';

function AnnotationsChangeHandlerFactory(): EditorPlugin {
  let lastRenderedAnnotations: EditorMetadata['annotations'] = {};
  const lastRenderedAnnotationText = new Map<AnnotationKey, string>();

  function updateAnnotationText(ahaEditor: AhaEditor) {
    const { editor } = ahaEditor;
    const provider = ahaEditor.getProvider('annotations');
    if (!editor.metadata?.annotations || !provider) return;

    const { annotations } = editor.metadata;
    const annotationValues = Object.values<Annotation | undefined>(
      annotations
    ).filter(Boolean);

    for (const annotation of annotationValues) {
      if (!isAnnotationKey(annotation.key)) continue;
      if (annotation.key === LOCAL_ONLY_ANNOTATION_KEY) continue;

      const currentText = Annotations.getAnnotatedText(editor, annotation);
      const previousText = lastRenderedAnnotationText.get(annotation.key);

      if (previousText !== undefined && previousText !== currentText) {
        provider.onAnnotationTextChanged(annotation.key, currentText);
      }

      lastRenderedAnnotationText.set(annotation.key, currentText);
    }
  }

  const debouncedUpdateAnnotationText = debounce(updateAnnotationText, 300);

  function MutateAnnotations(ahaEditor: AhaEditor) {
    const { editor } = ahaEditor;
    const provider = ahaEditor.getProvider('annotations');
    if (!editor.metadata?.annotations || !provider) return false;

    const { annotations } = editor.metadata;

    if (Object.values(lastRenderedAnnotations).length > 0) {
      const annotationValues = Object.values<Annotation | undefined>(
        annotations
      ).filter(Boolean);

      for (const annotation of annotationValues) {
        if (!isAnnotationKey(annotation.key)) continue;
        const previousAnnotation = lastRenderedAnnotations[annotation.key];
        if (!previousAnnotation?.range?.current) continue;

        const previousRange = previousAnnotation.range.current;
        // Annotations require an expanded selection when created, the only way
        // for the range to be collapsed is that the user removed the nodes
        const previousNodesPresent = Range.isExpanded(previousRange);
        const currentRange = annotation.range.current;

        const currentNodesPresent =
          currentRange && Range.isExpanded(currentRange);

        const annotationNodesRemoved =
          previousNodesPresent && !currentNodesPresent;

        if (annotationNodesRemoved) {
          if (annotation.key === LOCAL_ONLY_ANNOTATION_KEY) {
            Annotations.removeAnnotation(editor);
            provider.onAnnotationCanceled();
          } else if (
            !annotation.wasCut &&
            annotation.kind === 'comment' &&
            !annotation.resolved
          ) {
            Annotations.changeAnnotationResolved(editor, {
              key: annotation.key,
              resolved: true,
            });
            provider.onAnnotationResolved(annotation.key);
            // TODO:ANALYTICS Track analytics
            // AhaAnalytics.track(
            //   'Feature-6805943453734376864',
            //   'User deletes linked text'
            // );
          }
        }

        const annotationNodesAdded =
          !previousNodesPresent && currentNodesPresent;

        if (annotationNodesAdded && !annotation.wasCut) {
          provider.onAnnotationShown(annotation.key);

          if (
            previousAnnotation.kind === 'comment' &&
            !previousAnnotation.resolved
          ) {
            Annotations.changeAnnotationResolved(editor, {
              key: annotation.key,
              resolved: false,
            });
            provider.onAnnotationUnresolved(annotation.key);
          }
        }
      }
    }

    debouncedUpdateAnnotationText(ahaEditor);

    lastRenderedAnnotations = cloneDeep(annotations);
  }

  const AnnotationsChangeHandler = {
    id: 'annotations-change-handler',
    name: 'Annotations change handler',
    handlers: {
      onChange: MutateAnnotations,
    },
  };

  return AnnotationsChangeHandler;
}

export default AnnotationsChangeHandlerFactory;
